/**
 * The accounts screen: a tenant managing their own upstream mail.
 *
 * This used to be an operator console wearing a user's clothes -- it asked for
 * a token that administers every tenant on the box, made the person choose
 * which tenant they were, and fell back to printing a CLI command whenever the
 * daemon had no endpoint. All three are gone. `/accounts` is authenticated by
 * the same session that reads the mail and takes the tenant from it, so there
 * is nothing to paste and nothing to choose, and every action on this screen
 * happens over HTTP. The CLI is still there for provisioning and for the
 * morning the daemon will not start; it is not where account management lives.
 *
 * Two things drive the layout. Adding an account is the highest-friction
 * moment in any mail client, so the credential is proved against the real
 * server before anything is stored and a wrong password comes back as a
 * sentence rather than as a sync failure noticed an hour later. And an account
 * that has stopped working must say so where someone will see it: a revoked
 * Google grant stops mail silently and forever, so it gets a banner and a
 * button rather than a red dot.
 *
 * # Sending is a state of the account, not a page in a wizard
 *
 * An IMAP account with no submission host cannot send at all, and until now the
 * only way to give it one was a shell. That state is now on the account's own
 * panel rather than only inside a dialog, because it is a thing that is *wrong* with
 * an account -- indistinguishable, from the outside, from a mail client that
 * has stopped working -- and somebody looking for why they cannot reply is
 * looking at this screen, not at a form they have not opened.
 *
 * # Two panels: the accounts, and the account
 *
 * The screen is a list of accounts beside the selected one's detail. It used to
 * be a stack of cards, which put every account's banners, statistics, colour
 * picker and Remove button on screen at once and grew a screenful with each
 * account somebody added -- so the thing they came here to fix was somewhere in
 * the middle of a page they had to read to find it. A list answers "which
 * accounts are there, and is anything wrong with one of them" at a glance, and
 * the panel beside it answers everything else about exactly one of them.
 *
 * Exactly one, which is also the safety property. Removing an account deletes
 * its stored credential and, through a foreign key that cascades, every message
 * ever collected for it. That button now sits underneath the name, the address
 * and the message count of the account it will act on, and the confirmation
 * says the name and the count out loud -- because a detail panel is precisely
 * the arrangement in which somebody can be looking at one account while
 * thinking about another.
 *
 * `<Accounts />` fills whatever region it is handed and knows nothing about
 * what is beside it. The settings shell puts a column of setting groups to its
 * left and gives this component an undecorated box; the panels, their headers
 * and their padding are all in here, which is what lets the same component be
 * the whole screen on a phone.
 *
 * Below `56.25rem` there is room for one panel, so there is one: the list, then
 * the detail, with a way back. That is the width the rest of the app collapses
 * at, and the query is imported rather than retyped -- the CSS that hides a
 * panel and the JavaScript that moves focus into it have to agree about which
 * panel is on screen, and two copies of a breakpoint agree until one is tuned.
 *
 * The Sending section guesses a host, offers the two ports that cover nearly
 * everything, and in the edit dialog has a Test button that dials the server
 * and says which of the two settings was wrong. There is no password field in
 * it and there must never be one: submission signs in with the account's own
 * IMAP login, and a second stored secret would be a second thing to rotate.
 *
 * # Adding an account configures both directions or neither
 *
 * It is one section (`SendingSection`) in both dialogs, and in the add dialog
 * it is switched on with a guessed host. Adding an account and then discovering
 * separately that it cannot reply was two jobs only because the daemon had two
 * endpoints; to the person doing it, "set up my mail" was always one.
 *
 * The guess is defensible only because nothing here is stored on trust. The
 * daemon proves the IMAP login, then proves the submission host with the same
 * credential, and writes nothing at all unless both answered -- so a guess that
 * is wrong costs a sentence on this screen, which is the cheapest place in the
 * system to find out. The reverse arrangement, storing what was typed and
 * discovering it at the first reply, is exactly the failure this screen already
 * has a warning banner for.
 *
 * All-or-nothing is also why an outbound refusal does not lose anybody's work:
 * nothing was saved, so the dialog is still open with every field still in it,
 * and the refusal carries a `problem` that says which half failed. That turns
 * into the one offer worth making -- add it without sending for now -- rather
 * than a form somebody has to fill in twice.
 */

import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

import * as api from "../lib/accounts";
import type {
  AccountView,
  OAuthReturn,
  SentCopy,
  SubmissionPatch,
  SubmissionSecurity,
  TestProblem,
  TestResult,
} from "../lib/accounts";
import * as auth from "../lib/auth";
import * as compose from "../lib/compose";
import * as nav from "../lib/navigation";
import * as palette from "../lib/palette";
import * as store from "../lib/store";
import type { Mailbox } from "../lib/types";
import * as Icon from "./Icons";

const rows = signal<AccountView[] | null>(null);
const error = signal<string | null>(null);
/** The account with an action in flight, so only its buttons go quiet. */
const busyId = signal<number | null>(null);
const verdicts = signal<Map<number, TestResult>>(new Map());
const confirming = signal<number | null>(null);
const oauthNote = signal<OAuthReturn | null>(null);

/**
 * The account the detail panel is showing, or null for none.
 *
 * A signal beside `rows` rather than anything the shell around this screen
 * knows about. Which account is open is this screen's own business -- a
 * settings shell that held it would have to hold one for every screen it ever
 * grows -- and keeping it here is also what makes the two panels a single
 * component that can be dropped into a region and left alone.
 *
 * Null is a real state and not a gap: on one panel it *is* the list, and on two
 * it is what {@link NothingChosen} draws.
 */
const selectedId = signal<number | null>(null);

/**
 * Whether both panels fit at once.
 *
 * The app's own three-column query, so this and the stylesheet cannot disagree
 * about how many panels are on screen -- they would the first time either was
 * tuned alone, and the disagreement is not cosmetic: it decides whether
 * choosing an account moves focus, and moving focus into a panel that CSS has
 * hidden loses the keyboard entirely.
 *
 * It settles three things and nothing else: whether an account is chosen for
 * you, whether the Back button exists, and where focus goes when the detail
 * changes.
 */
const wideQuery = window.matchMedia(nav.THREE_COLUMN);
const twoPanel = signal(wideQuery.matches);
wideQuery.addEventListener("change", (e) => (twoPanel.value = e.matches));

type Dialog =
  | { kind: "add" }
  | { kind: "edit"; id: number; focus?: "password" | "host" | "submission" }
  | { kind: "oauth"; provider: string; name: string; reconnect?: string };
const dialog = signal<Dialog | null>(null);

/**
 * Take the Google outcome, at import time.
 *
 * Not in an effect. In a browser the query string is the only carrier of this,
 * the daemon put it there on a top-level navigation, and by the time a component
 * mounts anything else in the app may have replaced it. Subscribing here also
 * means the outcome survives a person landing on the mail view first: the screen
 * they started the flow from is the screen they come back to.
 *
 * A subscription rather than a read, because a desktop build is handed this
 * through a URL scheme whenever the person finishes with Google, rather than in
 * the page load that brought them back. The browser transport still delivers it
 * synchronously from right here, so nothing changes for the browser.
 */
api.onOAuthReturn((back) => {
  oauthNote.value = back;
  store.view.value = "accounts";
});

export function Accounts() {
  useEffect(() => {
    void refresh();
    // `/ready` says whether this deployment has a Google client at all. The
    // login screen asks for it too, but someone who is already signed in never
    // saw that screen in this tab.
    if (auth.capability.value === null) void auth.refreshCapability();
  }, []);

  const list = rows.value;
  const wide = twoPanel.value;
  // Resolved from the list rather than trusted, so an account that has just
  // been removed -- or renamed out from under a stale id by another tab --
  // cannot leave the detail panel drawing something that is not there.
  const chosen =
    list?.find((account) => account.id === selectedId.value) ?? null;

  // Nothing chosen is the list on one panel and a hole on two, so the wide
  // layout fills it as soon as there is anything to fill it with. Re-run on the
  // width as well as on the list: a window dragged past the breakpoint gains a
  // panel that would otherwise sit empty until the next click.
  useEffect(() => {
    if (wide) preselect();
  }, [list, wide]);

  return (
    <div class="acct-panes" data-showing={chosen ? "detail" : "list"}>
      <AccountList list={list} chosen={chosen} />
      {chosen ? (
        // Keyed, so the colour panel one account left open does not reappear on
        // the next one holding the previous account's hex.
        <Detail key={chosen.id} account={chosen} wide={wide} />
      ) : (
        <NothingChosen list={list} />
      )}

      {dialog.value?.kind === "add" && <AddDialog />}
      {/*
        Keyed by account, so the sending fields cannot carry one account's
        submission host into another's form if two dialogs are ever opened
        without a close between them.
      */}
      {dialog.value?.kind === "edit" && <EditDialog key={dialog.value.id} />}
      {dialog.value?.kind === "oauth" && <OAuthDialog />}
    </div>
  );
}

// ---- the list of accounts ------------------------------------------------------

/**
 * Every account, and the way to add another.
 *
 * A list of rows and not a listbox. A row is a button that changes what the
 * panel beside it shows, which is what `aria-current` describes and what the
 * rest of this app already uses for exactly this -- the open message in the
 * message list, the current mailbox in the sidebar. `aria-selected` would
 * promise a selection somebody can extend, and there is only ever one account
 * on screen to the right of this.
 *
 * The screen-level failures live here rather than in the detail panel: a list
 * that could not be read has no account to be about, and Try again is a
 * question about the list.
 */
function AccountList({
  list,
  chosen,
}: {
  list: AccountView[] | null;
  chosen: AccountView | null;
}) {
  const providers = auth.capability.value?.oauthProviders ?? [];

  return (
    <div class="acct-list-pane">
      <div class="acct-list-head">
        <h2 id="acct-list-title">Accounts</h2>
        {/*
          The count earns the header its place. The shell above this says
          "Accounts" too, on the narrow layout where it is the whole screen, and
          a header that only repeated that word would be a line of chrome; how
          many there are is a thing the list itself takes scrolling to answer.
        */}
        {list !== null && list.length > 0 && (
          <span class="acct-list-count">{list.length}</span>
        )}
      </div>

      <div class="acct-list-body">
        <OAuthNote />

        {error.value && (
          <div class="acct-alert bad" role="alert">
            <strong>{error.value}</strong>
            <button class="btn" onClick={() => void refresh()}>
              Try again
            </button>
          </div>
        )}

        {list === null && !error.value && (
          <p class="acct-list-note">
            <span class="spinner" />{" "}
            <span class="hint">Reading your accounts…</span>
          </p>
        )}

        {list?.length === 0 && <p class="acct-list-note">No accounts yet.</p>}

        {list && list.length > 0 && (
          <div
            class="acct-list"
            role="list"
            aria-labelledby="acct-list-title"
            onKeyDown={moveFocus}
          >
            {list.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                chosen={account === chosen}
              />
            ))}
          </div>
        )}

        <div class="acct-add">
          <button class="btn primary" onClick={(e) => open(e, { kind: "add" })}>
            <Icon.Plus />
            Add an IMAP account
          </button>
          {providers.map((kind) => (
            <button
              key={kind}
              class="btn"
              onClick={(e) =>
                open(e, {
                  kind: "oauth",
                  provider: kind,
                  name: freeOAuthName(kind),
                })
              }
            >
              Connect a {api.PROVIDER_LABEL[kind] ?? kind} account
            </button>
          ))}
          {/*
            The promise about the credential is here, beside the button that
            asks for one, rather than in a paragraph at the top of the screen
            that was read once and scrolled past.
          */}
          {/*
            Named one at a time rather than as "no OAuth is configured": a
            deployment commonly has one registration and not the other, and a
            single sentence covering both would be wrong about the half that
            works.
          */}
          {!providers.includes("gmail") && (
            <p class="hint">
              Google sign-in is not configured on this server: set{" "}
              <code>HM_GOOGLE_CLIENT_ID</code> and <code>HM_GOOGLE_CLIENT_SECRET</code>{" "}
              (see <code>docs/gmail-oauth.md</code>). Meanwhile, add it over IMAP with an app
              password.
            </p>
          )}
          {!providers.includes("microsoft") && (
            <p class="hint">
              Microsoft sign-in is not configured on this server: set{" "}
              <code>HM_MICROSOFT_CLIENT_ID</code> and{" "}
              <code>HM_MICROSOFT_CLIENT_SECRET</code> (see{" "}
              <code>docs/microsoft-oauth.md</code>). Outlook.com does not accept IMAP passwords.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountRow({
  account,
  chosen,
}: {
  account: AccountView;
  chosen: boolean;
}) {
  const flag = flagFor(account);

  return (
    <div role="listitem">
      <button
        id={rowDomId(account.id)}
        class="acct-row"
        aria-current={chosen}
        // The same stripe a message row carries, from the same map, so the
        // colour chosen in the panel beside this is already doing its job in
        // the place it was chosen.
        style={
          { "--acct-colour": palette.colourVar(account.colour) } as Record<
            string,
            string
          >
        }
        onClick={() => select(account)}
      >
        <span class={`dot ${dotFor(account)}`} aria-hidden="true" />
        <span class="acct-row-text">
          <span class="acct-row-name">{account.name}</span>
          <span class="acct-row-sub">{account.login ?? "no address"}</span>
          {(account.syncing || flag) && (
            <span class="acct-row-tags">
              {account.syncing && <span class="tag acct-live">syncing</span>}
              {flag && <span class="tag acct-flag">{flag}</span>}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

/**
 * The nothing-chosen panel, which only ever appears beside the list.
 *
 * On one panel this state *is* the list -- the detail is hidden, and hidden by
 * `visibility`, so it is out of the accessibility tree as well as out of sight.
 * So this can say "on the left" and mean it, and it is the one place left with
 * room to say what the screen is for.
 */
function NothingChosen({ list }: { list: AccountView[] | null }) {
  return (
    <div class="acct-detail-pane">
      <div class="acct-detail-body acct-intro">
        {/*
          What the screen is, and not what an account is -- the sentence about
          the credential is beside the button that asks for one, where somebody
          is in a position to care about it.
        */}
        <p class="lede">
          Every mailbox this daemon collects for you, and everything about how
          it collects it: where each one signs in, whether it can send, what it
          has fetched so far, and the colour it wears in the mail views.
        </p>
        {list !== null &&
          (list.length === 0 ? (
            <p class="hint">
              No accounts yet. Add one on the left.
            </p>
          ) : (
            <p class="hint">
              Choose an account on the left.
            </p>
          ))}
      </div>
    </div>
  );
}

// ---- one account -------------------------------------------------------------

/**
 * Everything about the account that is chosen.
 *
 * The head is not scrolled away with the rest of it. Which account this is is
 * the thing every button below it acts on, including the one that deletes mail,
 * and on one panel it is also the only thing on screen saying that the list has
 * been replaced rather than lost.
 */
function Detail({ account, wide }: { account: AccountView; wide: boolean }) {
  const pane = useRef<HTMLDivElement>(null);
  const verdict = verdicts.value.get(account.id);
  const busy = busyId.value === account.id;
  const maildir = account.provider === "maildir";
  const verdictId = `acct-verdict-${account.id}`;
  const nameId = `acct-detail-name-${account.id}`;

  // On one panel the list has just been replaced by this, and the button that
  // was pressed is now `visibility: hidden` -- which drops focus to the top of
  // the document and takes a screen reader's place with it. The panel takes it
  // instead, and announces which account it is on the way in.
  //
  // On two panels nothing was replaced, and moving focus would pull the
  // keyboard out of a list somebody is still walking down.
  useEffect(() => {
    if (!wide) pane.current?.focus();
  }, [account.id]);

  return (
    <div
      class="acct-detail-pane"
      ref={pane}
      tabIndex={-1}
      role="region"
      aria-labelledby={nameId}
    >
      <div class="acct-detail-head">
        {!wide && (
          <button
            class="icon-btn"
            aria-label="Back to accounts"
            onClick={backToList}
          >
            <Icon.Back />
          </button>
        )}
        <div class="acct-head">
          <span class={`dot ${dotFor(account)}`} aria-hidden="true" />
          <span class="grow">
            <div class="acct-name" id={nameId}>
              {account.name}
              <span class="tag">{providerLabel(account.provider)}</span>
              {account.syncing && <span class="tag acct-live">syncing</span>}
            </div>
            <div class="acct-sub">{identityLine(account)}</div>
          </span>
        </div>
      </div>

      <div class="acct-detail-body">
        {account.auth === "needs_reauth" && (
          <div class="acct-alert bad" role="alert">
            {/*
              The grant is revoked -- by a changed password, a security review, or
              ninety days without use. Which of those it was is not something the
              reader can act on; that syncing again will not help, and that
              reconnecting will, are.
            */}
            <strong>Google is no longer accepting this account.</strong>
            <p>
              Nothing is being collected, and syncing again will not change
              that. Reconnecting takes one trip to Google's consent screen.
            </p>
            <button
              class="btn primary"
              onClick={(e) =>
                open(e, {
                  kind: "oauth",
                  provider: account.provider,
                  name: account.name,
                  reconnect: account.name,
                })
              }
            >
              Reconnect {account.name}
            </button>
          </div>
        )}

        {account.auth === "no_credential" && !maildir && (
          <div class="acct-alert warn">
            <strong>No stored credential.</strong>
            <p>
              This account exists but has nothing to sign in with, so it is not
              collecting mail.
              {isOAuth(account.provider)
                ? ` Connect it to ${providerLabel(account.provider)} to give it one.`
                : " Set a password to give it one."}
            </p>
            <button
              class="btn"
              onClick={(e) =>
                isOAuth(account.provider)
                  ? open(e, {
                      kind: "oauth",
                      provider: account.provider,
                      name: account.name,
                      reconnect: account.name,
                    })
                  : open(e, { kind: "edit", id: account.id, focus: "password" })
              }
            >
              {isOAuth(account.provider)
                ? `Connect to ${providerLabel(account.provider)}`
                : "Set a password"}
            </button>
          </div>
        )}

        <CannotSend account={account} />

        <dl class="acct-stats">
          <div>
            <dt>Last sync</dt>
            <dd>{lastSyncLine(account)}</dd>
          </div>
          <div>
            <dt>Messages</dt>
            <dd>{account.messages.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Folders</dt>
            <dd>{account.mailboxes.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Added</dt>
            <dd>
              {account.createdAt
                ? new Date(account.createdAt * 1000).toLocaleDateString()
                : "—"}
            </dd>
          </div>
        </dl>

        {account.lastRun?.error && account.auth !== "needs_reauth" && (
          <p class="acct-error">{account.lastRun.error}</p>
        )}

        {account.consecutiveFailures > 1 && (
          <p class="hint">
            {account.consecutiveFailures} failed runs in a row. Test the
            connection to see why.
          </p>
        )}

        <ColourPicker account={account} />

        {/*
          The path is an operator's to set: one chosen here would be a path this
          daemon then reads on the tenant's behalf, which is a different privilege
          from owning a mailbox.
        */}
        {maildir && (
          <p class="hint">
            A Maildir on the server
            {account.settings.path ? (
              <>
                {" "}
                — <code>{account.settings.path}</code>
              </>
            ) : null}
            . Only an operator can change the path.
          </p>
        )}

        <FolderRoleOverrides account={account} />

        <div class="acct-actions">
          <button
            class="btn"
            disabled={busy}
            aria-describedby={verdict ? verdictId : undefined}
            onClick={() => void runTest(account)}
          >
            Test connection
          </button>
          <button
            class="btn"
            disabled={busy || account.syncing}
            onClick={() => void sync(account)}
          >
            <Icon.Refresh />
            {account.syncing ? "Syncing…" : "Sync now"}
          </button>
          {!maildir && (
            <button
              class="btn"
              disabled={busy}
              onClick={(e) => open(e, { kind: "edit", id: account.id })}
            >
              Edit
            </button>
          )}
          <span class="grow" />
          {confirming.value === account.id ? (
            <>
              {/*
                An alert, because arming this replaces the button that was
                pressed -- the DOM node is gone and focus with it, so a reader
                who is listening rather than looking would otherwise be asked a
                question about deleting mail and never hear it.
              */}
              <span class="hint acct-confirm" role="alert">
                {removalQuestion(account)}
              </span>
              <button
                class="btn danger"
                disabled={busy}
                onClick={() => void destroy(account)}
              >
                Remove
              </button>
              <button
                class="btn acct-keep"
                disabled={busy}
                onClick={() => (confirming.value = null)}
              >
                Keep
              </button>
            </>
          ) : (
            <button
              class="btn danger"
              disabled={busy}
              onClick={() => arm(account)}
            >
              Remove
            </button>
          )}
        </div>

        {busy && <span class="spinner" />}

        {verdict && (
          <div
            id={verdictId}
            class={`acct-alert ${verdict.ok ? "good" : "bad"}`}
            role="status"
            aria-live="polite"
          >
            <strong>
              {verdict.ok ? "That worked." : "That did not work."}
            </strong>
            <p>{verdict.message}</p>
            {!verdict.ok && (
              <NextStep account={account} problem={verdict.problem} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "This account cannot send", on the account's panel, before anyone opens
 * anything.
 *
 * The state it describes is invisible everywhere else until the moment somebody
 * tries to reply, and then it presents as a mail client that does not work.
 * Reading mail and relaying mail are separate services and an account genuinely
 * often has only the first -- so this is a warning and not an error, and it says
 * what the account *does* do before it says what it does not.
 *
 * Maildir gets nothing: its own panel already explains that it is a directory
 * on the server, and "a directory cannot relay mail" is not news to anyone
 * reading that.
 *
 * Gmail gets nothing either, and that is a change. This used to say sending
 * from a Google account was not built; it is, over XOAUTH2, and the session
 * attaches a submitter to every Google account it opens. So there is no host,
 * no port and no second credential to choose -- Google's submission server is
 * the same one for everybody and the token that reads the mail sends it. An
 * account that can send and needs no configuration has nothing to warn about,
 * and a warning that survives the thing it warned about is worse than none: it
 * sends people looking for a setting that does not exist.
 */
function CannotSend({ account }: { account: AccountView }) {
  if (account.provider === "maildir" || account.settings.submission)
    return null;
  if (account.provider !== "imap") return null;

  return (
    <div class="acct-alert warn">
      <strong>This account cannot send mail.</strong>
      {/*
        Outgoing mail goes through a different server from reading, usually the
        same name with `smtp` in front -- which the form below guesses, so the
        panel does not need to teach it. What it does need to say is that no
        second password is involved, because that is the thing someone puts off
        going to look for.
      */}
      <p>
        It collects mail, but nothing is set up to send it, so the composer will
        refuse to send from this account. Sending signs in with this account's
        own login, so there is no second password to find.
      </p>
      <button
        class="btn primary"
        onClick={(e) =>
          open(e, { kind: "edit", id: account.id, focus: "submission" })
        }
      >
        Set up sending
      </button>
    </div>
  );
}

/**
 * The eight colours and a ninth of your own, next to the account they belong to.
 *
 * A row of buttons rather than a `<select>` of colour names, because choosing
 * between colours is a thing done by looking, and a dropdown hides seven of the
 * eight until it is opened. They are real buttons, so Tab reaches the group and
 * arrow keys are not needed to leave it, and each one's accessible name is the
 * colour's own -- "Teal", not a hex string. That is the reason the daemon
 * stores words in the first place: a swatch has to be able to say what it is.
 *
 * `aria-pressed` marks the current one as well as the ring around it, so the
 * selection is not carried by colour either.
 *
 * The ninth button is a different kind of control and says so: it opens a panel
 * instead of setting a colour, so it carries `aria-expanded` rather than
 * `aria-pressed`, and the panel itself is what says whether this account is on a
 * custom colour today. A button announcing "pressed, expanded" would be
 * describing two different things with one word.
 *
 * Deliberately past the end of the eight rather than in place of them. A
 * built-in colour is guaranteed to stay distinguishable from the other seven
 * under red-green colour blindness and a colour off a wheel cannot be, so the
 * palette stays the path of least resistance and the wheel is one press further
 * away -- which is the whole of the accessibility argument that a picker can
 * honour without refusing anybody anything.
 *
 * The write is optimistic in one direction only. The row's own colour is
 * repainted from the server's answer, but `store.setAccountColours` is called
 * with that answer too, which is what makes the sidebar and the message list
 * follow along without a reload -- they read the same map, and it is the only
 * copy.
 */
function ColourPicker({ account }: { account: AccountView }) {
  const busy = busyId.value === account.id;
  const headingId = `acct-colour-${account.id}`;
  const panelId = `acct-custom-${account.id}`;
  const custom = palette.isCustom(account.colour);
  const [open, setOpen] = useState(false);

  return (
    <div class="acct-colour-field">
      <div class="label" id={headingId}>
        Colour
      </div>
      <div class="acct-swatches" role="group" aria-labelledby={headingId}>
        {palette.COLOURS.map((colour) => (
          <button
            key={colour}
            class="acct-swatch"
            style={
              { "--acct-colour": palette.colourVar(colour) } as Record<
                string,
                string
              >
            }
            aria-pressed={account.colour === colour}
            aria-label={palette.COLOUR_NAMES[colour]}
            title={palette.COLOUR_NAMES[colour]}
            disabled={busy}
            onClick={() => void recolour(account, colour)}
          />
        ))}
        <button
          class={`acct-swatch acct-swatch-own${custom ? " own-in-use" : ""}`}
          style={
            custom
              ? ({
                  "--acct-colour": palette.colourVar(account.colour),
                } as Record<string, string>)
              : undefined
          }
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={
            custom ? `Custom colour, ${account.colour}` : "Custom colour"
          }
          title="Custom colour"
          disabled={busy}
          onClick={() => setOpen(!open)}
        />
      </div>
      {open && <CustomColour account={account} id={panelId} />}
    </div>
  );
}

/**
 * Picking a colour that is not one of the eight.
 *
 * Two inputs for one value, which is not a redundancy: `<input type="color">`
 * opens the platform's own picker, which is the right thing for a person who
 * wants to *look* at colours and is a locked door to anybody driving this with
 * a keyboard or a screen reader, because what it opens is an operating system
 * dialog this page cannot describe. The text field is the same value in a form
 * that can be typed, pasted out of a brand guide, read back, and copied to
 * somebody else.
 *
 * # What the two swatches are for
 *
 * They are the honest part. A colour typed in here is not necessarily the
 * colour that gets stored: `palette.harmonise` pins its lightness into the band
 * the eight occupy so that it reads on both backgrounds, which can move a pale
 * yellow a long way. Rather than adjust it silently -- or worse, adjust it
 * *while somebody is dragging around a colour wheel*, which is why the picker's
 * own value is left exactly as chosen -- the panel shows both results against
 * the two backgrounds they will be drawn on, and says what will be stored.
 *
 * The backgrounds are hard-coded rather than themed, deliberately: the whole
 * purpose of these two boxes is to show the theme you are *not* in.
 */
function CustomColour({ account, id }: { account: AccountView; id: string }) {
  const busy = busyId.value === account.id;
  // Whatever is in the box, exactly as typed -- not a colour. Half of what
  // somebody types on the way to `#4f46e5` is not a colour yet, and a state that
  // could only hold valid ones would have to reject keystrokes to stay valid.
  const [typed, setTyped] = useState<string>(
    palette.isCustom(account.colour) ? account.colour : "#4f46e5",
  );

  const picked = palette.readCustom(typed);
  const pair = picked ? palette.harmonise(picked) : null;
  // The native picker cannot hold "not a colour yet", and half of what somebody
  // types on the way to a hex string is exactly that. It keeps the last thing
  // that parsed rather than jumping to a default while a character is deleted.
  const lastValid = useRef(picked ?? "#4f46e5");
  if (picked) lastValid.current = picked;

  // The other accounts, so a colour that cannot be told apart from one of them
  // can be mentioned. Advice, not a refusal -- see `lib/palette.ts` -- and it is
  // measured in the theme on screen, because that is the comparison the person
  // reading it is able to make.
  const others = (rows.value ?? []).filter((a) => a.id !== account.id);
  const clash =
    pair === null
      ? null
      : palette.nearest(
          pair.light,
          others.map((a) => a.colour),
        );
  const clashing = clash === null ? null : others[clash];

  return (
    <div class="acct-own" id={id}>
      <div class="field">
        <label for={`${id}-hex`}>Colour</label>
        <div class="acct-own-inputs">
          <input
            id={`${id}-hex`}
            type="text"
            value={typed}
            spellcheck={false}
            autocomplete="off"
            placeholder="#4f46e5"
            aria-describedby={`${id}-hint`}
            onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
          />
          <input
            class="acct-own-wheel"
            type="color"
            aria-label="Pick a colour"
            value={lastValid.current}
            onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
          />
        </div>
        <span class="hint" id={`${id}-hint`}>
          Six hex digits, or use the picker beside the box.
        </span>
      </div>

      {pair === null ? (
        <p class="hint">
          Not a colour — six hex digits, like #4f46e5.
        </p>
      ) : (
        <>
          <div class="acct-own-previews">
            <div class="acct-own-preview" style={{ background: "#ffffff" }}>
              <span class="swatch" style={{ background: pair.light }} />
              <span style={{ color: "#16181d" }}>Light theme</span>
            </div>
            <div class="acct-own-preview" style={{ background: "#15171c" }}>
              <span class="swatch" style={{ background: pair.dark }} />
              <span style={{ color: "#e6e8ec" }}>Dark theme</span>
            </div>
          </div>
          <p class="hint">
            The dark-mode colour is derived from <code>{pair.light}</code>.
          </p>
          {clashing && (
            <p class="hint acct-own-clash">
              Hard to tell apart from {clashing.name}.
            </p>
          )}
          <div class="acct-actions">
            <button
              class="btn primary"
              type="button"
              disabled={busy || account.colour === pair.light}
              onClick={() => void recolour(account, pair.light)}
            >
              Use this colour
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---- which folder is which -------------------------------------------------------

/**
 * Say which folder plays each role, where the daemon's guess is wrong or absent.
 *
 * # Why a person ever has to do this
 *
 * The daemon reads SPECIAL-USE attributes and, where a server publishes none,
 * matches folder names in English. Both of the ways that fails are on this
 * server today. An account whose folders are "Enviados" and "Papelera" gets no
 * roles at all -- no Sent, no Trash, no Archive -- so the Archive button has
 * nowhere to file and the unified views are empty for a mailbox that is full.
 * And where there are two candidates the wrong one can win: a Gmail account
 * here had a user folder called "Sent" beside `[Gmail]/Sent Mail`, and the
 * unified Sent view showed two messages instead of 495.
 *
 * Detection stays and stays first. This is a repair tool, which is why it is
 * behind a disclosure like the device token in Settings rather than on the
 * panel: an account where the guess is right -- almost all of them -- should
 * never have to read this, and an account where it is wrong is unusable until
 * somebody finds it.
 *
 * # Detected and chosen are not the same state, and the screen has to say so
 *
 * Someone opening this needs to know whether a role is a guess that could
 * change under them or a decision they made, because those two answer the
 * question they came with -- "why is my mail in the wrong place" -- in
 * opposite ways. So each row says which of the two it is and names the folder
 * either way, and the select itself carries the same distinction in its value:
 * "Detect automatically" is a real option and not a blank.
 *
 * A role with no folder at all is the third state, and it is the one the
 * feature exists for. It says what is broken by it rather than reporting a
 * dash.
 *
 * # Only an existing account, and only after it has synced
 *
 * There are no folders to choose between until a sync has listed them, which
 * is why this is nowhere near the add dialog -- the same reasoning that keeps
 * "Copies in Sent" out of it. An account that has not synced says so here
 * rather than offering an empty select.
 *
 * # Applied as a batch, not per row
 *
 * Every change is held until Save and only the changed keys are sent, because
 * a patch of all five would pin the four nobody touched to whatever detection
 * currently says -- silently, and for good. The batch also lets two roles trade
 * folders in one request: applied a row at a time, the first half of a swap is
 * two roles on one folder, which the daemon refuses, and there would be no way
 * through it from this screen.
 */
function FolderRoleOverrides({ account }: { account: AccountView }) {
  // The same `Mailbox/get` list the sidebar draws, keyed by the JMAP account
  // id -- which is the decimal spelling of this account's own id. Reusing it
  // rather than asking the daemon for a folder list means the names offered
  // here are exactly the names on the left of the screen, and there is no
  // second request that could disagree with the first.
  const boxes = store.mailboxes.value.get(String(account.id)) ?? [];
  const stored = api.folderRolesOf(account.settings);
  // Only the roles that differ from what is stored, so this object *is* the
  // patch. Anything else -- absolute values, or a copy of all five -- would
  // have to be diffed at save time, and a diff that is subtly wrong here pins
  // roles nobody chose.
  const [pending, setPending] = useState<Partial<api.FolderRoles>>({});
  // The daemon's own sentence, kept beside this control rather than in the
  // screen-level `error` signal: it is about the folder somebody just chose,
  // and the panel-wide alert is about the list.
  const [refusal, setRefusal] = useState<string | null>(null);
  // Whether *this* control is the one in flight. `busy` is the account's, and
  // it is set by Sync now and Test connection as well -- a button that said
  // "Saving…" because a sync was running would be describing somebody else's
  // work as this form's.
  const [saving, setSaving] = useState(false);
  const busy = busyId.value === account.id;

  const choices = folderChoices(boxes);
  const nameOf = (id: string): string | null =>
    choices.find((choice) => choice.id === id)?.path ?? null;
  const dirty = Object.keys(pending).length > 0;
  const chosenCount = api.FOLDER_ROLES.filter(
    (role) => stored[role] !== null,
  ).length;

  const valueOf = (role: api.FolderRole): string | null =>
    Object.prototype.hasOwnProperty.call(pending, role)
      ? (pending[role] ?? null)
      : stored[role];

  function choose(role: api.FolderRole, next: string | null): void {
    setRefusal(null);
    setPending((current) => {
      const out = { ...current };
      // Choosing what is already stored is not a change, and leaving it in
      // would send a key that means "pin this" for a role that is already
      // pinned to it -- harmless on the wire and a lie in the Save button,
      // which is the only thing saying whether there is anything to save.
      if (next === stored[role]) delete out[role];
      else out[role] = next;
      return out;
    });
  }

  async function save(): Promise<void> {
    const patch = pending;
    busyId.value = account.id;
    setSaving(true);
    setRefusal(null);
    try {
      await api.update(account.id, { folderRoles: patch });
      setPending({});
      store.toast(rolesSavedMessage(patch, account, nameOf));
      await refresh();
      // The roles moved. Every unified view and every folder row is drawn from
      // the session's mailbox list, which is where a role now lives, so this is
      // the moment the sidebar is wrong until it is re-read.
      await store.refreshSession();
    } catch (e) {
      // Left exactly as it was chosen. The daemon refuses before it writes, so
      // nothing has changed and the selections are still the ones worth
      // correcting -- clearing them would make the reader start again.
      setRefusal(message(e));
    } finally {
      setSaving(false);
      busyId.value = null;
    }
  }

  return (
    <details class="acct-roles">
      <summary>
        Advanced: which folder is which
        {/*
          On the summary, so a role somebody pinned is visible without opening
          anything. A closed disclosure that hides the fact that Sent is a
          decision rather than a guess is the same failure this whole section
          is about, one level up.
        */}
        {chosenCount > 0 && (
          <span class="tag">{chosenCount} chosen by you</span>
        )}
      </summary>

      <p class="hint">
        Correct a role here if a folder was not recognised.
      </p>

      {choices.length === 0 ? (
        // Not an empty select. An account that has never synced has no folders
        // to name, and a control with one option in it invites somebody to
        // conclude the folder they are looking for has been lost.
        //
        // Two ways of having none, and they are not the same news. The panel
        // above this says how many folders the daemon has collected, so a
        // reader whose account says 19 and whose select says none needs to be
        // told that the disagreement is this tab's and not their account's.
        <p class="hint">
          {account.mailboxes > 0
            ? "Folder list not loaded yet — reload the page."
            : "No folders collected yet."}
        </p>
      ) : (
        <>
          {api.FOLDER_ROLES.map((role) => (
            <FolderRoleRow
              key={role}
              role={role}
              account={account}
              boxes={boxes}
              choices={choices}
              value={valueOf(role)}
              storedValue={stored[role]}
              busy={busy}
              onChoose={(next) => choose(role, next)}
            />
          ))}

          {refusal && (
            <div class="acct-alert bad" role="alert">
              <strong>That was refused.</strong>
              {/*
                The daemon's sentence and nothing else. It names the folder, the
                two roles, or the account the folder actually belongs to -- and
                a client that replaced it with "Could not save" would be
                throwing away the only help this control has.
              */}
              <p>{refusal}</p>
            </div>
          )}

          <div class="acct-actions">
            <button
              class="btn primary"
              type="button"
              disabled={busy || !dirty}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save folder roles"}
            </button>
            {dirty && (
              <button
                class="btn"
                type="button"
                disabled={busy}
                onClick={() => setPending({})}
              >
                Discard changes
              </button>
            )}
            {/*
              Said where the button is, because "only what you changed" is not
              observable from the screen and the alternative -- five roles
              pinned by opening a panel -- is exactly what a reader would fear
              from a form like this one.
            */}
            <span class="hint">Only the roles you changed are sent.</span>
          </div>
        </>
      )}
    </details>
  );
}

/**
 * One role: what is in force, and the folders it could be instead.
 *
 * A `<select>` rather than the radios the rest of this screen prefers, and for
 * once the dropdown is right: one live account here has nineteen folders, the
 * options need no sentence to choose between, and a nested folder is named by
 * its whole path so that "Clients / Acme / Invoices" is one line rather than an
 * indent a screen reader does not read out.
 */
function FolderRoleRow({
  role,
  account,
  boxes,
  choices,
  value,
  storedValue,
  busy,
  onChoose,
}: {
  role: api.FolderRole;
  account: AccountView;
  boxes: Mailbox[];
  choices: FolderChoice[];
  /** What the select is showing, which is the pending choice where there is one. */
  value: string | null;
  /** What the server has, so an unsaved change can say that it is unsaved. */
  storedValue: string | null;
  busy: boolean;
  onChoose: (next: string | null) => void;
}) {
  const id = `acct-role-${account.id}-${role}`;
  const hintId = `${id}-hint`;
  const nameOf = (mailboxId: string): string | null =>
    choices.find((choice) => choice.id === mailboxId)?.path ?? null;
  // A stored override naming a folder that is not in the list -- renamed away,
  // deleted, or on an account whose last sync predates it. Without an option to
  // match, the select would fall back to showing its first one, which is
  // "Detect automatically": the screen would claim the opposite of the truth.
  const missing = value !== null && nameOf(value) === null;

  return (
    <div class="field acct-role">
      <label for={id}>{ROLE_LABELS[role]}</label>
      <select
        id={id}
        value={value ?? ""}
        disabled={busy}
        aria-describedby={hintId}
        onChange={(e) => {
          const next = (e.target as HTMLSelectElement).value;
          onChoose(next === "" ? null : next);
        }}
      >
        {/*
          A named option and not a blank one. "Detect automatically" is the
          state most accounts are in and the state somebody withdraws a choice
          by returning to, so it has to be selectable and has to say what it
          means.
        */}
        <option value="">Detect automatically</option>
        {missing && value !== null && (
          <option value={value}>
            A folder this account no longer has ({value})
          </option>
        )}
        {choices.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.path}
          </option>
        ))}
      </select>
      <span class="hint" id={hintId}>
        {inForceLine(role, value, storedValue, boxes, nameOf)}
      </span>
    </div>
  );
}

const ROLE_LABELS: Record<api.FolderRole, string> = {
  sent: "Sent",
  archive: "Archive",
  drafts: "Drafts",
  trash: "Trash",
  junk: "Junk",
};

/**
 * What breaks while a role has no folder at all.
 *
 * Named per role rather than as one sentence about "some features", because
 * the reader arrived here from one of these symptoms and the whole job of the
 * line is to connect the symptom to the row that fixes it.
 */
const ROLE_MISSING: Record<api.FolderRole, string> = {
  sent: "the unified Sent view is empty, and a copy of a message you send has nowhere to be filed",
  archive: "the Archive button has nowhere to file",
  drafts: "the composer cannot save a draft",
  trash: "deleting a message has nowhere to move it to",
  junk: "nothing can be marked as junk",
};

/**
 * The sentence under one role's select: which folder is in force, and whether
 * that is a guess, a decision, or nothing.
 *
 * The detected name is read from the mailbox list rather than remembered,
 * because "detected" and "in force" are the same thing whenever nothing is
 * overridden -- the daemon publishes the role it settled on, however it settled
 * on it. Which also means there is no honest way to show what detection *would*
 * say underneath a choice that is overruling it, and this does not pretend
 * otherwise.
 */
function inForceLine(
  role: api.FolderRole,
  value: string | null,
  storedValue: string | null,
  boxes: Mailbox[],
  nameOf: (id: string) => string | null,
): string {
  if (value !== storedValue) {
    // Unsaved, so nothing about the account has changed yet and the line must
    // not describe the new state as though it had.
    return value === null
      ? "Not saved yet — this will go back to being detected automatically."
      : `Not saved yet — this will become ${nameOf(value) ?? "a folder that is not in the list"}.`;
  }
  if (value !== null) {
    const name = nameOf(value);
    return name === null
      ? "You chose a folder this account no longer has. Choose another, or go back to detecting it."
      : `You chose this: ${name}. Detection cannot change it.`;
  }
  const detected = boxes.find((box) => box.role === role);
  return detected
    ? `Detected automatically: ${detected.name}.`
    : `Nothing detected, so ${ROLE_MISSING[role]}.`;
}

/** A folder to choose from: its id, and the whole path a person reads it by. */
interface FolderChoice {
  id: string;
  path: string;
}

/**
 * Every folder of one account, depth first, each named by its full path.
 *
 * Paths rather than an indent, because an indent in a `<select>` is whitespace:
 * it is what a screen reader skips and what a narrow phone dropdown truncates
 * from the wrong end. "Clients / Acme / Invoices" says the same thing in a form
 * that survives both, and it also disambiguates the case this whole feature is
 * about -- a user folder called "Sent" beside `[Gmail]/Sent Mail`.
 *
 * The two malformations the sidebar's own tree has to survive apply here for
 * the same reason: a `parentId` naming a folder that is not in the list is
 * ordinary (Gmail's `[Gmail]` is `\Noselect`), and a cycle is possible because
 * `parentId` is whatever the provider said. Orphans are treated as roots, and
 * a cycle's members are never reached by the walk -- no member of one is a
 * root -- so they are appended afterwards under their own names. A bad link
 * costs the path, never the folder: a folder missing from this list is a role
 * that cannot be repaired.
 */
function folderChoices(boxes: Mailbox[]): FolderChoice[] {
  const byId = new Map(boxes.map((box) => [box.id, box]));
  const children = new Map<string, Mailbox[]>();
  const roots: Mailbox[] = [];

  for (const box of boxes) {
    if (box.parentId === null || !byId.has(box.parentId)) {
      roots.push(box);
      continue;
    }
    const kids = children.get(box.parentId);
    if (kids) kids.push(box);
    else children.set(box.parentId, [box]);
  }

  const out: FolderChoice[] = [];
  const seen = new Set<string>();
  const walk = (list: Mailbox[], above: string[]): void => {
    for (const box of [...list].sort((a, b) => a.name.localeCompare(b.name))) {
      const segments = [...above, box.name];
      seen.add(box.id);
      out.push({ id: box.id, path: readablePath(segments) });
      walk(children.get(box.id) ?? [], segments);
    }
  };
  walk(roots, []);
  for (const box of boxes)
    if (!seen.has(box.id)) out.push({ id: box.id, path: box.name });
  return out;
}

/**
 * A path deep enough to be truncated, shortened where it costs least.
 *
 * A `<select>` on a phone truncates from the end, which is the end that
 * identifies the folder: "Receipts / Clients / Acme / Invoices / Paid /
 * Disputed" becomes a row of ancestors and no answer. Dropping the middle keeps
 * the folder's own name, its parent -- which is what tells two folders of the
 * same name apart -- and the account-level folder they are all under.
 *
 * Only past four levels. Below that the whole path fits in the width this
 * control has, and an ellipsis buys a character and loses a fact.
 */
function readablePath(segments: string[]): string {
  if (segments.length <= 4) return segments.join(" / ");
  return [segments[0], "…", ...segments.slice(-2)].join(" / ");
}

/**
 * What the toast says after roles were saved.
 *
 * One change gets named, because one change is what nearly every visit here
 * makes and the reader wants to hear the folder back. Several get counted:
 * three clauses in a toast that disappears in four seconds is a sentence
 * nobody finishes, and the panel behind it now says all of it anyway.
 */
function rolesSavedMessage(
  patch: Partial<api.FolderRoles>,
  account: AccountView,
  nameOf: (id: string) => string | null,
): string {
  const keys = Object.keys(patch) as api.FolderRole[];
  const only = keys.length === 1 ? keys[0] : undefined;
  if (!only) return `Saved which folder is which for ${account.name}.`;
  const value = patch[only] ?? null;
  return value === null
    ? `Saved. ${ROLE_LABELS[only]} is detected automatically again.`
    : `Saved. ${ROLE_LABELS[only]} is now ${nameOf(value) ?? "the folder you chose"}.`;
}

/**
 * What to offer after a failed test.
 *
 * Driven by `problem` rather than by the prose, which is what the server's
 * stable vocabulary is for. Each case names one thing to go and do; where
 * nothing the client can offer would help -- an unreachable host, a bad
 * certificate -- it says so instead of opening a form that changes the wrong
 * field.
 */
function NextStep({
  account,
  problem,
}: {
  account: AccountView;
  problem?: TestProblem;
}) {
  switch (problem) {
    case "credentials":
      return (
        <button
          class="btn"
          onClick={(e) =>
            open(e, { kind: "edit", id: account.id, focus: "password" })
          }
        >
          Re-enter the password
        </button>
      );
    case "reauth":
    case "no_credential":
      return isOAuth(account.provider) ? (
        <button
          class="btn"
          onClick={(e) =>
            open(e, {
              kind: "oauth",
              provider: account.provider,
              name: account.name,
              reconnect: account.name,
            })
          }
        >
          Reconnect with {providerLabel(account.provider)}
        </button>
      ) : (
        <button
          class="btn"
          onClick={(e) =>
            open(e, { kind: "edit", id: account.id, focus: "password" })
          }
        >
          Set a password
        </button>
      );
    case "unreachable":
    case "tls":
      return account.provider === "imap" ? (
        <button
          class="btn"
          onClick={(e) =>
            open(e, { kind: "edit", id: account.id, focus: "host" })
          }
        >
          Check the server address
        </button>
      ) : null;
    // Every one of these is about where the account *sends*, which is a
    // different form from the one above it and a different address to check.
    // Including the credentials case: the fix for a submission server refusing
    // the login is often an app password that the IMAP side does not need, and
    // sending someone to the password box would have them retype the one that
    // is working.
    case "submission_unreachable":
    case "submission_security":
    case "submission_credentials":
    case "submission_tls":
    case "submission_config":
      return account.provider === "imap" ? (
        <button
          class="btn"
          onClick={(e) =>
            open(e, { kind: "edit", id: account.id, focus: "submission" })
          }
        >
          Check where this account sends
        </button>
      ) : null;
    default:
      return null;
  }
}

// ---- the Google round trip ----------------------------------------------------

function OAuthNote() {
  const note = oauthNote.value;
  if (!note) return null;
  return (
    <div
      class={`acct-alert ${note.outcome === "connected" ? "good" : "warn"}`}
      role="status"
    >
      <strong>
        {note.outcome === "connected"
          ? `Connected ${note.account ?? "a Google account"}.`
          : "Google authorisation was cancelled."}
      </strong>
      <p>
        {note.outcome === "connected"
          ? "The first sync starts on the daemon's next pass; press Sync now if you would rather not wait."
          : "Nothing was changed."}
      </p>
      <button class="btn" onClick={() => (oauthNote.value = null)}>
        Dismiss
      </button>
    </div>
  );
}

function OAuthDialog() {
  const d = dialog.value;
  if (d?.kind !== "oauth") return null;
  const reconnect = d.reconnect;
  const who = api.PROVIDER_LABEL[d.provider] ?? d.provider;

  return (
    <Dialog
      title={reconnect ? `Reconnect ${reconnect}` : `Connect a ${who} account`}
    >
      <p class="hint">
        You sign in with {who} and come back here. HydraMail stores a token, never your
        password.
      </p>
      {!reconnect && (
        <div class="field">
          <label for="g-name">Name this account</label>
          <input
            id="g-name"
            type="text"
            value={d.name}
            data-autofocus
            onInput={(e) =>
              (dialog.value = {
                ...d,
                name: (e.target as HTMLInputElement).value,
              })
            }
          />
          <span class="hint">
            Shown in your folder list. Must be unique.
          </span>
        </div>
      )}
      <div class="acct-actions">
        <button
          class="btn primary"
          disabled={!d.name.trim()}
          data-autofocus={reconnect ? "" : undefined}
          onClick={() => api.startOAuth(d.provider, d.name.trim())}
        >
          Continue to {who}
        </button>
        <button class="btn" onClick={close}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

// ---- adding and editing --------------------------------------------------------

function AddDialog() {
  const name = useRef<HTMLInputElement>(null);
  const port = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  // The two fields the guessed submission host is derived from, so they are
  // state rather than refs: a guess made from the host as it was two keystrokes
  // ago is a guess about somebody else's server.
  const [host, setHost] = useState("");
  const [user, setUser] = useState("");
  // On by default, with a host guessed from the two boxes above. Defaulting to
  // off would be the cautious-looking choice and the wrong one: almost every
  // account somebody adds to a mail client is one they intend to reply from,
  // and the state this whole flow exists to stop producing is the account that
  // silently cannot send. What makes the guess safe to make *for* someone is
  // that it is never stored on trust -- the daemon signs in to it before
  // anything is written, so a wrong guess is a sentence on this screen rather
  // than a message that fails to leave next Tuesday.
  const sending = useSending({
    on: true,
    host: "",
    port: "465",
    security: "implicit",
    guessed: true,
    verdict: null,
  });
  // Set when the daemon accepted the login and refused the submission host, so
  // the one thing worth offering next can be offered.
  const [outboundRefused, setOutboundRefused] = useState(false);
  const busy = busyId.value === -1;

  /** Keep the guess following the fields it is a guess from, until it is edited. */
  function guessFrom(nextHost: string, nextUser: string): void {
    if (sending.guessed)
      sending.patch({ host: api.guessSubmissionHost(nextHost, nextUser) });
  }

  async function add(withSending: boolean): Promise<void> {
    busyId.value = -1;
    error.value = null;
    setOutboundRefused(false);
    try {
      const added = await api.create({
        name: name.current?.value.trim() ?? "",
        host: host.trim(),
        port: Number(port.current?.value ?? 993),
        user: user.trim(),
        password: password.current?.value ?? "",
        ...(withSending ? { submission: submissionOf(sending) } : {}),
      });
      close();
      store.toast(
        added.settings.submission
          ? `Added ${added.name}. Both servers accepted the login, so it can collect mail and send it.`
          : `Added ${added.name}. It signed in, so the first sync will work.`,
      );
      await refresh();
      // Onto the account that was just added, on both layouts. It is the thing
      // that was being worked on, it is the one whose first sync somebody is
      // about to watch, and on one panel it saves them finding it in a list
      // they have not seen yet.
      select(added);
      await store.refreshSession();
    } catch (err) {
      // The daemon's own sentence, which already ends in "Nothing was saved."
      // And nothing was: the dialog stays open with every field exactly as it
      // was typed, which is what makes an all-or-nothing create affordable.
      error.value = message(err);
      setOutboundRefused(api.isSubmissionProblem(err));
    } finally {
      busyId.value = null;
    }
  }

  return (
    <Dialog title="Add an IMAP account">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void add(sending.on);
        }}
      >
        <div class="field">
          <label for="a-name">Name</label>
          <input
            id="a-name"
            ref={name}
            type="text"
            placeholder="personal"
            data-autofocus
            required
          />
          <span class="hint">Must be unique.</span>
        </div>
        <div class="field">
          <label for="a-host">Server</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="a-host"
              type="text"
              value={host}
              placeholder="imap.example.org"
              required
              onInput={(e) => {
                const next = (e.target as HTMLInputElement).value;
                setHost(next);
                guessFrom(next, user);
              }}
            />
            <input
              ref={port}
              type="number"
              style={{ width: 96 }}
              value={993}
              aria-label="Port"
              min={1}
              max={65535}
            />
          </div>
          {/*
            No STARTTLS option for reading, and there should not be one: it
            opens in cleartext, anyone on the path can strip the upgrade, and
            the password follows on the same connection.
          */}
          <span class="hint">
            Implicit TLS only.
          </span>
        </div>
        <div class="field">
          <label for="a-user">Username</label>
          <input
            id="a-user"
            type="text"
            value={user}
            autocomplete="off"
            required
            onInput={(e) => {
              const next = (e.target as HTMLInputElement).value;
              setUser(next);
              guessFrom(host, next);
            }}
          />
          <span class="hint">Usually the full address.</span>
        </div>
        <div class="field">
          <label for="a-pass">Password</label>
          <input
            id="a-pass"
            ref={password}
            type="password"
            autocomplete="new-password"
            required
          />
          <span class="hint">
            Checked against the server before saving. Providers with two-factor
            authentication need an app password here.
          </span>
        </div>

        {/*
          No Test button here, unlike the edit dialog: there is no account yet
          to test one against, and inventing an endpoint that dials an arbitrary
          host on a tenant's say-so is a bigger thing than this needs. The
          primary button below *is* the test -- it proves both servers and
          stores nothing unless both answered -- so the only difference is that
          the answer arrives once rather than twice.
        */}
        <SendingSection ids="a-sub" sending={sending} />

        {error.value && (
          <div class="acct-alert bad" role="alert">
            <strong>{error.value}</strong>
            {outboundRefused && (
              <>
                <p>
                  The mail server accepted the login; only the sending details
                  were refused. Correct them above, or add the account now and
                  set sending up later from its own panel.
                </p>
                <button
                  class="btn"
                  type="button"
                  disabled={busy}
                  onClick={() => void add(false)}
                >
                  Add it without sending for now
                </button>
              </>
            )}
          </div>
        )}
        <div class="acct-actions">
          <button class="btn primary" type="submit" disabled={busy}>
            {busy
              ? "Signing in…"
              : sending.on
                ? "Test both and add"
                : "Test and add"}
          </button>
          <button class="btn" type="button" onClick={close} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Change what an account is, without ever being handed what it already has.
 *
 * The password box starts empty and an empty box means "keep the stored one" --
 * there is no way to prefill it, because the server will not serve a
 * credential, and a box showing eight dots that are not the real password is a
 * lie a person will act on.
 *
 * Sending is the second half, and it is deliberately in the same dialog rather
 * than behind its own button: it is one account, the two servers are configured
 * with the same three facts, and a person who has just fixed a password is
 * often the same person about to find out that sending was never set up.
 */
function EditDialog() {
  const d = dialog.value;
  const account = rows.value?.find(
    (a) => a.id === (d?.kind === "edit" ? d.id : -1),
  );
  const name = useRef<HTMLInputElement>(null);
  const host = useRef<HTMLInputElement>(null);
  const port = useRef<HTMLInputElement>(null);
  const user = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);

  // Where this account sends. Real state rather than refs, because unlike every
  // other field here these ones affect each other: the security choice moves the
  // port, and turning sending off hides the rest.
  //
  // A stored `plaintext` is an operator's doing and cannot be chosen here, so it
  // starts on the setting saving would move it to -- and says so, in the
  // section itself, rather than changing it quietly.
  const stored = account?.settings.submission ?? null;
  const sending = useSending({
    on: stored !== null,
    host:
      stored?.host ??
      (account
        ? api.guessSubmissionHost(account.settings.host ?? null, account.login)
        : ""),
    port: String(stored?.port ?? 465),
    security: stored?.security === "starttls" ? "starttls" : "implicit",
    guessed: stored === null,
    verdict: null,
  });
  const [testing, setTesting] = useState(false);

  // Who files the copy in Sent. Its own state rather than part of `sending`,
  // because it is the one field in that section which is not a fact about the
  // submission server's address -- nothing about it invalidates a Test verdict,
  // and `useSending` clears the verdict on every change by design.
  //
  // Seeded from what the server sent, defaulting to the state that inherits, so
  // an account from a server that predates this setting reads as "not answered"
  // rather than as an answer nobody gave. The dialog is keyed by account id, so
  // this seeds once per account rather than going stale.
  const storedSentCopy = account?.settings.sentCopy ?? "provider";
  const [sentCopy, setSentCopy] = useState<SentCopy>(storedSentCopy);

  if (!account || d?.kind !== "edit") return null;

  const imap = account.provider === "imap";
  const busy = busyId.value === account.id;
  const candidate = submissionOf(sending);
  const changedSubmission = !sending.on
    ? stored !== null
    : stored === null ||
      stored.host !== candidate.host ||
      stored.port !== candidate.port ||
      stored.security !== candidate.security;

  async function runSubmissionTest(): Promise<void> {
    if (!account) return;
    setTesting(true);
    try {
      // The candidate, not what is stored: the whole point is to find out
      // before saving. The daemon dials it, signs in, sends nothing and writes
      // nothing whichever way it goes.
      sending.patch({ verdict: await api.test(account.id, candidate) });
    } catch (e) {
      sending.patch({
        verdict: { ok: false, problem: "unknown", message: message(e) },
      });
    } finally {
      setTesting(false);
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (!account) return;
    const patch: api.AccountPatch = {};
    const put = (
      key: keyof api.AccountPatch,
      value: string | number | undefined,
      was: unknown,
    ) => {
      if (value !== undefined && value !== "" && value !== was) {
        (patch as Record<string, unknown>)[key] = value;
      }
    };
    put("name", name.current?.value.trim(), account.name);
    if (imap) {
      put("host", host.current?.value.trim(), account.settings.host);
      put("port", Number(port.current?.value), account.settings.port);
      put("user", user.current?.value.trim(), account.login);
      // Explicitly null to turn sending off. `put` cannot carry this: it skips
      // anything empty, and "leave it alone" and "stop sending" are the two
      // things that must not be confused here.
      if (changedSubmission) patch.submission = sending.on ? candidate : null;
    }
    if (password.current?.value) patch.password = password.current.value;
    // Outside the `imap` branch: a Google account has no host, port or password
    // to edit and this is the one thing about its sending that is not a
    // constant. Sent only when it moved, so opening the dialog and pressing
    // Save does not rewrite a setting nobody touched.
    if (sentCopy !== storedSentCopy) patch.sentCopy = sentCopy;

    if (Object.keys(patch).length === 0) {
      close();
      return;
    }

    busyId.value = account.id;
    error.value = null;
    try {
      const saved = await api.update(account.id, patch);
      close();
      // The daemon connects before it stores anything that could break
      // sign-in — including the submission host, which it dials and signs in to
      // — so a save that returned is a save that was proved.
      store.toast(`Saved ${saved.name}.`);
      verdicts.value = new Map(verdicts.value).set(saved.id, {
        ok: true,
        message: sendingSavedMessage(patch),
      });
      // The composer asks the server whether an account can send and remembers
      // the answer. This is the moment that answer stopped being true.
      if (patch.submission !== undefined)
        compose.forgetSubmission(String(saved.id));
      await refresh();
      await store.refreshSession();
    } catch (err) {
      error.value = message(err);
    } finally {
      busyId.value = null;
    }
  }

  return (
    <Dialog title={`Edit ${account.name}`}>
      <form onSubmit={submit}>
        <div class="field">
          <label for="e-name">Name</label>
          <input
            id="e-name"
            ref={name}
            type="text"
            value={account.name}
            data-autofocus={d.focus ? undefined : ""}
            required
          />
        </div>

        {imap ? (
          <>
            <div class="field">
              <label for="e-host">Server</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  id="e-host"
                  ref={host}
                  type="text"
                  value={account.settings.host ?? ""}
                  data-autofocus={d.focus === "host" ? "" : undefined}
                />
                <input
                  ref={port}
                  type="number"
                  style={{ width: 96 }}
                  value={account.settings.port ?? 993}
                  aria-label="Port"
                  min={1}
                  max={65535}
                />
              </div>
            </div>
            <div class="field">
              <label for="e-user">Username</label>
              <input
                id="e-user"
                ref={user}
                type="text"
                value={account.login ?? ""}
                autocomplete="off"
              />
              {/* The two are stored as one credential, so one cannot move without the other. */}
              <span class="hint">
                Changing this means re-entering the password.
              </span>
            </div>
            <div class="field">
              <label for="e-pass">New password</label>
              <input
                id="e-pass"
                ref={password}
                type="password"
                autocomplete="new-password"
                placeholder="leave blank to keep the stored one"
                data-autofocus={d.focus === "password" ? "" : undefined}
              />
              {/*
                No hint under this one. It said that the stored password is not
                shown here and cannot be -- the daemon will not serve a
                credential back to this page -- which is true and is already the
                whole content of the placeholder sitting in the empty box.
              */}
            </div>

            <SendingSection
              ids="e-sub"
              sending={sending}
              autofocus={d.focus === "submission"}
              storedSecurity={stored?.security}
              sentCopy={{ value: sentCopy, onChange: setSentCopy }}
            >
              <div class="acct-actions">
                <button
                  class="btn"
                  type="button"
                  disabled={testing || busy || !sending.host.trim()}
                  aria-describedby={
                    sending.verdict ? "e-sub-verdict" : undefined
                  }
                  onClick={() => void runSubmissionTest()}
                >
                  {testing ? "Trying…" : "Test sending"}
                </button>
                <span class="hint">
                  Signs in and sends nothing.
                </span>
              </div>
            </SendingSection>
          </>
        ) : (
          <>
            <p class="hint">
              Google accounts sign in and send with a token; nothing to set up
              here. Use Reconnect if it has stopped working.
            </p>
            {/*
              Outside `SendingSection`, because a Google account has no
              submission server to configure and rendering an empty one to hang
              this off would invent a section with a single field in it. It is
              offered at all because Google's own filing is not something this
              server can take on trust: a real send through it has been seen to
              leave nothing in Sent, and an account that cannot say so has no
              way back to a Sent folder that records what it sent.
            */}
            <SentCopyField ids="e-gm" value={sentCopy} onChange={setSentCopy} />
          </>
        )}

        {error.value && (
          <p class="acct-error" role="alert">
            {error.value}
          </p>
        )}
        <div class="acct-actions">
          <button class="btn primary" type="submit" disabled={busy}>
            {busy ? "Checking…" : imap ? "Verify and save" : "Save"}
          </button>
          <button class="btn" type="button" onClick={close} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ---- where an account sends ------------------------------------------------------

/**
 * The three facts about a submission server, plus whether there is one.
 *
 * Held together in one object because they affect each other -- the security
 * choice moves the port, turning sending off hides the rest, and any of them
 * changing invalidates a verdict about the previous combination -- and every
 * one of those relationships is a bug waiting to happen if they are five
 * independent `useState` calls that someone later updates four of.
 */
interface Sending {
  on: boolean;
  host: string;
  /** A string, because it is what an `<input>` holds; "" while being retyped. */
  port: string;
  security: "implicit" | "starttls";
  /**
   * Whether `host` is this client's guess rather than something typed.
   *
   * Only the add form uses it, where the guess has to keep following the IMAP
   * host as it is typed and then stop the moment somebody corrects it. The edit
   * form guesses once, on an account whose host is not moving.
   */
  guessed: boolean;
  verdict: TestResult | null;
}

function useSending(initial: Sending) {
  const [state, setState] = useState(initial);
  // Every setter goes through here, and every one of them clears the verdict.
  // A green "that worked" left standing next to a port somebody has since
  // changed is worse than no verdict at all: it is a claim about a server this
  // form is no longer describing.
  const patch = (next: Partial<Sending>): void =>
    setState((current) => ({ ...current, verdict: null, ...next }));
  return { ...state, patch };
}

type SendingFields = ReturnType<typeof useSending>;

/** What the daemon is asked to store or to dial, from what is on screen. */
function submissionOf(sending: SendingFields): SubmissionPatch {
  return {
    host: sending.host.trim(),
    port: Number(sending.port) || (sending.security === "starttls" ? 587 : 465),
    security: sending.security,
  };
}

/**
 * Who put the copy in Sent -- asked as the symptom, not as the mechanism.
 *
 * # Why this is on screen at all
 *
 * When a message is submitted, either the account's own server files a copy in
 * Sent or it does not, and HydraMail has to do the opposite. Nobody can be told
 * which from the outside: a plain SMTP host usually files nothing and Google
 * usually files its own, but `amit@achaudhari.com` is a plain IMAP account
 * whose server files one anyway, so the sensible guess produced two of every
 * sent message. That is not a setting anyone would think to look for, which is
 * why the wording here leads with what they will have already noticed.
 *
 * # Three options and not a checkbox
 *
 * The first one is the important one. It means nobody has answered, and the
 * daemon uses what it knows about the provider -- which is right for most
 * accounts and is where every account on this server sits today. A checkbox
 * cannot hold it, and either way of forcing the question would break accounts
 * that are currently fine: default off and every Google account starts getting
 * two copies, default on and plain SMTP accounts silently stop being filed at
 * all, which is the worse of the two because nothing appears anywhere.
 *
 * Radios and not a `<select>` for the reason the encryption radios give one
 * section down: each option needs a sentence to choose between, and a dropdown
 * hides two of the three until it is opened.
 *
 * # Not offered when adding an account
 *
 * The answer is read off a Sent folder after a message has gone through the
 * account, so at the moment of adding one there is nothing to read it from.
 * Offering it there would collect a guess about the thing the default already
 * handles, and a wrong guess here duplicates mail. It appears once the account
 * exists, which is also when the symptom can appear.
 */
function SentCopyField({
  ids,
  value,
  onChange,
}: {
  ids: string;
  value: SentCopy;
  onChange: (next: SentCopy) => void;
}) {
  const options: { value: SentCopy; label: string; hint: string }[] = [
    {
      value: "provider",
      label: "Work it out from the provider",
      hint: "The default, and right for most accounts. Change it only if you see one of the two problems below.",
    },
    {
      value: "upstream",
      label: "My server files it — a duplicate appears in Sent",
      hint: "Choose this if every message you send shows up twice in Sent. HydraMail will stop adding its own copy.",
    },
    {
      value: "local",
      label: "My server does not — nothing appears in Sent",
      hint: "Choose this if a message you have sent is missing from Sent afterwards. HydraMail will file its own copy.",
    },
  ];

  return (
    <div class="field" role="radiogroup" aria-labelledby={`${ids}-copy-label`}>
      <div class="label" id={`${ids}-copy-label`}>
        Copies in Sent
      </div>
      {options.map((option) => (
        <label
          class="acct-check"
          for={`${ids}-copy-${option.value}`}
          key={option.value}
        >
          <input
            id={`${ids}-copy-${option.value}`}
            type="radio"
            name={`${ids}-copy`}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>
            {option.label}
            <span class="hint"> — {option.hint}</span>
          </span>
        </label>
      ))}
      {/*
        Said once, under the group, because it is the thing that makes this
        answerable rather than a preference: it is checkable in under a minute
        and the check is the same one whichever way it goes wrong.
      */}
      <span class="hint">
        Send yourself a message: one copy in Sent means this is right.
      </span>
    </div>
  );
}

/**
 * The Sending section, in the add dialog and the edit dialog both.
 *
 * One component and not two similar ones, because the whole point of this
 * change is that setting up sending is the same job whenever it is done -- and
 * two copies of a form is how the second one quietly stops offering STARTTLS a
 * year later. The differences between the two callers are the id prefix, what
 * the fields start out holding, and whether there is anything to press Test on.
 */
function SendingSection({
  ids,
  sending,
  autofocus,
  storedSecurity,
  sentCopy,
  children,
}: {
  ids: string;
  sending: SendingFields;
  autofocus?: boolean;
  /** A `plaintext` here is an operator's doing, and saying so is worth a line. */
  storedSecurity?: SubmissionSecurity;
  /**
   * Who files the copy in Sent, for a caller that has an account to ask about.
   *
   * Omitted by the add dialog, and the guard is here rather than in a second
   * copy of this section -- see {@link SentCopyField} for why the question
   * cannot be answered before the account has sent anything. This is the same
   * shape as `children`, which the add dialog also leaves out because it has no
   * account id to press Test against.
   */
  sentCopy?: { value: SentCopy; onChange: (next: SentCopy) => void };
  children?: ComponentChildren;
}) {
  /** Keep the port on the convention for the security setting, until it is typed in. */
  function chooseSecurity(next: "implicit" | "starttls"): void {
    const conventional =
      sending.port === "" || sending.port === "465" || sending.port === "587";
    sending.patch({
      security: next,
      ...(conventional ? { port: next === "starttls" ? "587" : "465" } : {}),
    });
  }

  return (
    <fieldset class="acct-sending">
      <legend>Sending</legend>
      <label class="acct-check" for={`${ids}-on`}>
        <input
          id={`${ids}-on`}
          type="checkbox"
          checked={sending.on}
          data-autofocus={autofocus ? "" : undefined}
          onChange={(e) =>
            sending.patch({ on: (e.target as HTMLInputElement).checked })
          }
        />
        <span>Send outgoing mail through this account</span>
      </label>

      {!sending.on ? (
        // A receive-only account is a normal thing to own -- a shared address,
        // an archive, a feed -- so this is a state, not a warning, and it does
        // not need defending on screen.
        <p class="hint" id={`${ids}-hint`}>
          Off: this account cannot send. It can be turned on later.
        </p>
      ) : (
        <>
          <div class="field">
            <label for={`${ids}-host`}>Submission server</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                id={`${ids}-host`}
                type="text"
                value={sending.host}
                placeholder="smtp.example.org"
                // Rather than a disabled submit button: an empty host with
                // sending switched on is the one combination this form can rule
                // out itself, and the browser's own "please fill in this field"
                // points at the box, which a greyed-out button does not.
                required
                aria-describedby={`${ids}-hint${sending.verdict ? ` ${ids}-verdict` : ""}`}
                onInput={(e) =>
                  sending.patch({
                    host: (e.target as HTMLInputElement).value,
                    guessed: false,
                  })
                }
              />
              <input
                type="number"
                style={{ width: 96 }}
                value={sending.port}
                aria-label="Submission port"
                min={1}
                max={65535}
                onInput={(e) =>
                  sending.patch({ port: (e.target as HTMLInputElement).value })
                }
              />
            </div>
            <span class="hint" id={`${ids}-hint`}>
              Guessed from the mail server; correct it if your provider says
              otherwise. Same login as above.
            </span>
          </div>

          {/*
            Two radios and not a select. There are exactly two answers, both
            need a sentence to choose between, and a dropdown hides the second
            one until it is opened -- on the question that is the single
            commonest way a submission host is wrong.
          */}
          <div
            class="field"
            role="radiogroup"
            aria-labelledby={`${ids}-sec-label`}
          >
            <div class="label" id={`${ids}-sec-label`}>
              Encryption
            </div>
            <label class="acct-check" for={`${ids}-implicit`}>
              <input
                id={`${ids}-implicit`}
                type="radio"
                name={`${ids}-security`}
                checked={sending.security === "implicit"}
                onChange={() => chooseSecurity("implicit")}
              />
              <span>
                Implicit TLS, normally port 465{" "}
                <span class="hint">— prefer this</span>
              </span>
            </label>
            <label class="acct-check" for={`${ids}-starttls`}>
              <input
                id={`${ids}-starttls`}
                type="radio"
                name={`${ids}-security`}
                checked={sending.security === "starttls"}
                onChange={() => chooseSecurity("starttls")}
              />
              <span>STARTTLS, normally port 587</span>
            </label>
            {/*
              STARTTLS here is required, never opportunistic: an upgrade a
              server can decline to offer is one an attacker on the path can
              decline for it, and the password follows on the same connection.
            */}
            <span class="hint">
              A mismatched port and encryption is the usual reason sending
              fails.
            </span>
          </div>

          {storedSecurity === "plaintext" && (
            <p class="hint">
              Currently sends unencrypted (set by an operator). Saving changes
              it to the setting above.
            </p>
          )}

          {children}

          {/*
            After the server fields and the Test button, because those are one
            job -- reaching the server at all -- and this is the one after it.
            Inside `sending.on` because an account that sends nothing files
            nothing, so the question does not arise until it does.
          */}
          {sentCopy && (
            <SentCopyField
              ids={ids}
              value={sentCopy.value}
              onChange={sentCopy.onChange}
            />
          )}
        </>
      )}

      {/*
        Always present, so a verdict arriving into it is an update to a region
        that already exists rather than a new one appearing -- which is what
        `aria-live` announces reliably.
      */}
      <div id={`${ids}-verdict`} role="status" aria-live="polite">
        {sending.verdict && (
          <div class={`acct-alert ${sending.verdict.ok ? "good" : "bad"}`}>
            <strong>
              {sending.verdict.ok ? "That worked." : "That did not work."}
            </strong>
            <p>{sending.verdict.message}</p>
          </div>
        )}
      </div>
    </fieldset>
  );
}

/**
 * A modal, with the focus behaviour a modal owes a keyboard.
 *
 * Focus moves in when it opens and returns to whatever opened it when it
 * closes, Escape closes it, and Tab cycles within it. Without the last one a
 * screen reader walks straight out of the dialog into the page behind it, which
 * is still there and still says "Remove".
 */
function Dialog({
  title,
  children,
}: {
  title: string;
  children: ComponentChildren;
}) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const first =
      box.current?.querySelector<HTMLElement>("[data-autofocus]") ??
      box.current?.querySelector<HTMLElement>(
        "input, button, select, textarea",
      );
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab" || !box.current) return;
      const focusable = [
        ...box.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const edge = e.shiftKey ? focusable[0] : focusable[focusable.length - 1];
      if (document.activeElement === edge) {
        e.preventDefault();
        (e.shiftKey ? focusable[focusable.length - 1] : focusable[0])?.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div class="overlay" onClick={close}>
      <div
        class="sheet acct-sheet"
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acct-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="acct-dialog-title" style={{ margin: "0 0 14px" }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// ---- choosing an account ----------------------------------------------------------

/** The row's element id, which is also how focus finds it again. */
function rowDomId(id: number): string {
  return `acct-row-${id}`;
}

function select(account: AccountView): void {
  selectedId.value = account.id;
  // An armed confirmation belongs to the account it was armed on. Carrying it
  // across is the exact mistake a detail panel makes easy to make, and the
  // button it arms deletes mail.
  confirming.value = null;
}

/**
 * Ask the question that Remove is really asking.
 *
 * Focus goes to Keep and not to Remove. Pressing this button unmounts it, so
 * focus has to be put somewhere deliberately or it falls to the top of the
 * document -- and the one place it must not be put is on a button that deletes
 * mail, where the second half of a double press would land on it.
 */
function arm(account: AccountView): void {
  confirming.value = account.id;
  focusSoon(".acct-keep");
}

/** Leave the detail on a one-panel layout, and put focus back where it came from. */
function backToList(): void {
  const id = selectedId.value;
  selectedId.value = null;
  confirming.value = null;
  if (id !== null) focusSoon(`#${rowDomId(id)}`);
}

/**
 * Choose an account when the layout has a panel to show one in.
 *
 * Only when nothing valid is chosen, so it cannot steal the panel from
 * somebody. The account someone has just come back from Google with wins over
 * the first one: this page load is the tail of a flow that was about that
 * account, and it is the one they are looking for.
 */
function preselect(): void {
  const list = rows.value ?? [];
  if (list.some((account) => account.id === selectedId.value)) return;
  const returned = oauthNote.value?.account;
  const first =
    (returned
      ? list.find((account) => account.name === returned)
      : undefined) ?? list[0];
  selectedId.value = first?.id ?? null;
}

/**
 * Up and down the list without a Tab per account.
 *
 * Focus only -- Enter or Space is what chooses. Selection that followed focus
 * would be pleasant on two panels and unusable on one, where every arrow press
 * would replace the list with a detail panel and take the keyboard with it.
 */
function moveFocus(event: KeyboardEvent): void {
  const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
  if (step === 0 && event.key !== "Home" && event.key !== "End") return;

  const stops = [
    ...(event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
      ".acct-row",
    ),
  ];
  const at = stops.indexOf(document.activeElement as HTMLElement);
  // Nothing in the list has focus -- a key repeat arriving after a row was
  // removed, most likely. Moving focus from wherever it actually is would be a
  // jump nobody asked for.
  if (at < 0) return;

  const to =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? stops.length - 1
        : at + step;
  const next = stops[Math.min(Math.max(to, 0), stops.length - 1)];
  if (!next) return;
  event.preventDefault();
  next.focus();
}

/**
 * Focus something that is not in the document yet.
 *
 * Every caller here has just unmounted the element focus was on -- a row that
 * became a detail panel, an account that no longer exists -- and Preact renders
 * on a microtask, so the replacement arrives after this turn. A frame later it
 * is there. Focus that is not moved deliberately at this moment lands on
 * `<body>`, which costs a keyboard the whole app to get back out of.
 */
function focusSoon(selector: string): void {
  requestAnimationFrame(() =>
    document.querySelector<HTMLElement>(selector)?.focus(),
  );
}

// ---- actions --------------------------------------------------------------------

/** Whatever had focus when a dialog opened, so it can have it back. */
let opener: HTMLElement | null = null;

function open(e: Event, next: Dialog): void {
  opener = (e.currentTarget as HTMLElement) ?? null;
  error.value = null;
  dialog.value = next;
}

function close(): void {
  dialog.value = null;
  error.value = null;
  opener?.focus();
  opener = null;
}

async function refresh(): Promise<void> {
  error.value = null;
  try {
    rows.value = await api.list();
    // The mail views read their colours from one map in the store, and this is
    // the screen that already has the document they come from. Publishing here
    // means a colour changed on this screen -- or an account added or removed
    // on it -- is right in the sidebar and the message list the moment someone
    // navigates back, without a second request for what was just fetched.
    store.setAccountColours(rows.value);
  } catch (e) {
    error.value = message(e);
  }
}

async function runTest(account: AccountView): Promise<void> {
  busyId.value = account.id;
  try {
    const result = await api.test(account.id);
    verdicts.value = new Map(verdicts.value).set(account.id, result);
    // A test that reports a revoked grant is news the list did not have; the
    // banner above the row is driven by `auth`, which only a re-read updates.
    if (result.problem === "reauth") await refresh();
  } catch (e) {
    verdicts.value = new Map(verdicts.value).set(account.id, {
      ok: false,
      problem: "unknown",
      message: message(e),
    });
  } finally {
    busyId.value = null;
  }
}

async function recolour(
  account: AccountView,
  colour: palette.AccountColour,
): Promise<void> {
  if (account.colour === colour) return;
  busyId.value = account.id;
  try {
    const saved = await api.update(account.id, { colour });
    // Patch the row in place rather than re-listing. A full refresh re-reads
    // every account's message counts and folder counts to repaint one dot, and
    // the visible cost of that -- the whole screen going quiet for a moment --
    // is the difference between a colour that feels like a setting and one that
    // feels like a form submission.
    rows.value = (rows.value ?? []).map((a) => (a.id === saved.id ? saved : a));
    store.setAccountColours(rows.value ?? []);
  } catch (e) {
    error.value = message(e);
  } finally {
    busyId.value = null;
  }
}

async function sync(account: AccountView): Promise<void> {
  busyId.value = account.id;
  try {
    switch (await api.syncNow(account.id)) {
      case "queued":
        store.toast(`Syncing ${account.name}. The result lands in Last sync.`);
        break;
      case "already-running":
        store.toast(`${account.name} is already syncing.`);
        break;
      case "unavailable":
        store.toast(
          "This daemon is not running a sync supervisor, so nothing can be queued.",
        );
        break;
    }
    await refresh();
  } catch (e) {
    error.value = message(e);
  } finally {
    busyId.value = null;
  }
}

async function destroy(account: AccountView): Promise<void> {
  busyId.value = account.id;
  try {
    await api.remove(account.id);
    confirming.value = null;
    // Before the refresh, so the detail panel is never showing an account the
    // list no longer has. On one panel this is also the way back: the thing
    // that was on screen is gone, and the list is what is left.
    selectedId.value = null;
    store.toast(
      `Removed ${account.name}, its stored credential and the mail collected for it.`,
    );
    await refresh();
    // Focus was on the Remove button, which has gone with the panel it was in.
    // The first row if there is one, and the button that adds an account if
    // that was the last one -- both of which are where somebody would go next.
    focusSoon(".acct-row, .acct-add .btn");
    // The mailbox list on the left is one account shorter now.
    await store.refreshSession();
  } catch (e) {
    error.value = message(e);
  } finally {
    busyId.value = null;
  }
}

// ---- rendering helpers ------------------------------------------------------------

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * What the panel says after a save, which depends on what was proved.
 *
 * The daemon dials whatever the patch could have broken before it stores it, so
 * this is a report of something that happened rather than a hope. Sending gets
 * its own sentence because it is the one people are least sure of, and "the
 * server accepted the login" is the fact they were looking for.
 */
function sendingSavedMessage(patch: api.AccountPatch): string {
  if (patch.submission === null) {
    return "Saved. This account no longer sends mail.";
  }
  if (patch.submission) {
    return `Saved. ${patch.submission.host} accepted the login, so this account can send now.`;
  }
  // Nothing was dialled for this one, and the message must not imply it was:
  // the daemon cannot check who files a copy in Sent without sending a message
  // on somebody's behalf, so it takes the answer and stores it. Only when it is
  // the whole patch -- otherwise the sentence about signing in is the true one,
  // and it is the one worth reading.
  if (patch.sentCopy && Object.keys(patch).length === 1) {
    return {
      provider: "Saved. Copies in Sent follow this account's provider again.",
      upstream:
        "Saved. HydraMail will leave the Sent copy to your server from now on.",
      local: "Saved. HydraMail will file its own copy in Sent from now on.",
    }[patch.sentCopy];
  }
  return "Saved. The daemon signed in with these details before storing them.";
}

/**
 * The one word a row can spare about an account that wants attention.
 *
 * The banners that say all of this properly are in the detail panel now, which
 * means they are one click away rather than on screen -- and an account that
 * has stopped collecting mail, or cannot send any, must not need a click to be
 * discovered. The dot beside the name carries the sync health; this carries the
 * two states the dot cannot, in the order they matter.
 *
 * Not a colour on its own: it is a word, in a tag, and a screen reader reads it
 * out as part of the row's own name.
 */
function flagFor(account: AccountView): string | null {
  if (account.auth === "needs_reauth") return "reconnect";
  if (account.auth === "no_credential" && account.provider !== "maildir")
    return "no sign-in";
  if (account.provider === "imap" && !account.settings.submission)
    return "cannot send";
  return null;
}

/**
 * The question, carrying both of the things that are about to go.
 *
 * `DELETE /accounts/:id` removes a row whose foreign keys cascade: the stored
 * credential, and every message ever collected for the account. The count is in
 * the sentence because it is the only number that says how much that is, and
 * because the panel this is asked in is one somebody can arrive at with a
 * different account in mind than the one on screen.
 */
function removalQuestion(account: AccountView): string {
  if (account.messages === 0) {
    return `Remove ${account.name} and its stored credential? It has collected no mail.`;
  }
  // Grouped, like the count in the statistics strip a few lines above it. The
  // same number written two ways on one screen reads as two numbers.
  const mail = `${account.messages.toLocaleString()} ${account.messages === 1 ? "message" : "messages"}`;
  return `Remove ${account.name}? Its stored credential and the ${mail} collected for it are deleted.`;
}

/**
 * Three states, and "never synced" is not either of the other two.
 *
 * An account added a minute ago is neither healthy nor failing, and colouring
 * it red sends someone looking for a problem that does not exist yet.
 */
function dotFor(account: AccountView): "ok" | "bad" | "idle" {
  if (account.auth !== "ok") return "bad";
  if (!account.lastRun) return "idle";
  if (account.lastRun.outcome === "ok") return "ok";
  if (account.lastRun.outcome === null) return "idle";
  return "bad";
}

function providerLabel(provider: string): string {
  return api.PROVIDER_LABEL[provider] ?? provider;
}

/**
 * Whether this account signs in through an authorisation flow rather than a
 * password.
 *
 * The question four places in this file ask, and the reason it is a function is
 * that `provider === "gmail"` was the answer in all four until it was not.
 */
function isOAuth(provider: string): boolean {
  return provider === "gmail" || provider === "microsoft";
}

/**
 * Who this account is, on one line.
 *
 * Only `login`, `host` and `port` -- three fields chosen by name. The server
 * re-emits `settings` field by field rather than passing its stored config
 * through, precisely so that a field added later is invisible until someone
 * exposes it, and spreading the object here would undo that from this end.
 */
function identityLine(account: AccountView): string {
  const parts = [account.login ?? "no address"];
  if (account.provider === "imap" && account.settings.host) {
    parts.push(`${account.settings.host}:${account.settings.port ?? 993}`);
  }
  // Named as a fourth field rather than folded into the host, because it is a
  // different server: reading and sending disagree about where they go far more
  // often than they agree.
  const submission = account.settings.submission;
  if (submission) parts.push(`sends via ${submission.host}:${submission.port}`);
  if (account.provider === "maildir" && account.settings.path)
    parts.push(account.settings.path);
  // Named rather than "signed in with OAuth": the point of the line is which
  // company holds the grant, and that is the thing someone goes to revoke.
  if (account.credential === "oauth2") {
    parts.push(`signed in with ${providerLabel(account.provider)}`);
  }
  return parts.join(" · ");
}

function lastSyncLine(account: AccountView): string {
  if (account.syncing) return "running now";
  const run = account.lastRun;
  if (!run) return "never";
  const at = ago(run.finished_at ?? run.started_at);
  switch (run.outcome) {
    case "ok": {
      const changed = run.created + run.updated + run.destroyed;
      return changed > 0 ? `${at} · ${changed} changes` : `${at} · nothing new`;
    }
    case "error":
      return `failed ${at}`;
    case "interrupted":
      return `interrupted ${at} — the daemon stopped mid-run`;
    default:
      return `started ${at}, still open`;
  }
}

/** The daemon reports run times as unix seconds, not the ISO strings JMAP uses. */
function ago(seconds: number | null | undefined): string {
  if (!seconds) return "at an unknown time";
  const elapsed = Math.max(0, Date.now() / 1000 - seconds);
  if (elapsed < 90) return "just now";
  if (elapsed < 3600) return `${Math.round(elapsed / 60)} min ago`;
  if (elapsed < 86_400) return `${Math.round(elapsed / 3600)} h ago`;
  return new Date(seconds * 1000).toLocaleDateString();
}

/** A name no existing account has, so a second one of a kind can be added. */
function freeOAuthName(kind: string): string {
  const taken = new Set((rows.value ?? []).map((a) => a.name));
  if (!taken.has(kind)) return kind;
  for (let n = 2; n < 50; n += 1)
    if (!taken.has(`${kind}-${n}`)) return `${kind}-${n}`;
  return "";
}
